import { normalizeHeaders, requestHeaders } from '@/api';
import { pmsClient } from '@/api/pmsClient';
import { TelecomService } from '@/services/telecom.service';
import { Logger } from '@montu-web/utilities';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';

const DEFAULT_POLLING_INTERVAL = 30000; // 30 seconds
const queryClient = pmsClient();
const logger = new Logger('PMS:useTelecomWaitingRoom');

interface UseTelecomWaitingRoomOptions {
  queuedSince?: DateTime;
  enabled?: boolean;
  pollingInterval?: number;
}

export const useTelecomWaitingRoom = ({
  enabled = true,
  pollingInterval = DEFAULT_POLLING_INTERVAL,
  queuedSince
}: UseTelecomWaitingRoomOptions = {}) => {
  const headers = useMemo(() => normalizeHeaders(requestHeaders()), []);
  useMemo(() => queuedSince && TelecomService.setWaitingRoomQuery(queuedSince), [queuedSince]);
  const [query, setQuery] = useState(TelecomService.getWaitingRoomQuery());
  const { isError, error, data } = queryClient.listTelecomWaitingRoomContract.useQuery({
    queryKey: ['telecomWaitingRoom', query.queuedSince],
    queryData: {
      query: query,
      headers: {
        ...headers,
        authorization: headers['authorization']
      }
    },
    enabled,
    refetchInterval: pollingInterval,
    staleTime: 0 // 100% to make sure each poll can get fresh data
  });

  const waitingRoom = useMemo(() => data?.body.data ?? [], [data?.body.data]);

  useEffect(() => {
    if (isError) {
      logger.error('There was an issue fetching waiting room data', error);
      return;
    }

    if (!waitingRoom.length) {
      return;
    }

    // no queuedSince means no fixed polling
    if (!queuedSince) {
      // order by queuedAt descending
      const [latest] = waitingRoom.toSorted((a, b) => b.queuedAt.localeCompare(a.queuedAt));
      // add 1 second since we are losing the MS from the server, this will prevent us from getting duplicates
      TelecomService.setWaitingRoomQuery(DateTime.fromISO(latest.queuedAt).plus({ second: 1 }).toUTC());
      setQuery(TelecomService.getWaitingRoomQuery());
    }
  }, [error, isError, queuedSince, waitingRoom]);

  return { waitingRoom };
};
